import {
    Heading,
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    Card,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    TextContainer,
    List,
    Stack,
    Select,
    HorizontalGrid,
    LegacyCard,
    MediaCard,
    Text,
    VerticalStack,
    LegacyStack,
    Link
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep, remove } from 'lodash';
import { useAppBridge, useToast } from "@shopify/app-bridge-react";
import { Redirect } from '@shopify/app-bridge/actions';
import { useLocation, useParams } from 'react-router-dom';
import Api from '../../helpers/api';
import debounce from 'lodash.debounce';
import {getSessionToken} from "@shopify/app-bridge-utils";
import jwtDecode from "jwt-decode";
import * as helpers from './helpers';
import {filterShippingMethods} from "../../helpers/utils";

const AddressDetails = ({address}) => {
    const parts = [
        `${address?.address1 || ''}`,
        `${address?.address2 || ''}`,
        `${address?.address3 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip_code || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Defined)</span>)
}

const Orders = () => {
    const search = useLocation().search;
    const app = useAppBridge();
    const redirect = Redirect.create(app);
    const {customerid} = useParams();
    const [locationId] = useState(
        (new URLSearchParams(search)).get('location_id') || null
    );

    const [customer, setCustomer] = useState(null);
    const [location, setLocation] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [additionalInfo, setAdditionalInfo] = useState({
        order_notes: '',
        customer_notes: ''
    });
    const [searchInput, setSearchInput] = useState('');
    const [errors, setErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);
    const [shippingMethods, setShippingMethods] = useState([]);
    const [shippingMethod, setShippingMethod] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [recommendedLineItems, setRecommendedLineItems] = useState([]);

    //This is trick that Rado provided, ask him, I don't know how it works :)
    //This function basically rounds up/down quantity to closest step
    const debounceQuantityUpdate = useCallback(debounce((key, quantity) => {
        setLineItems((lineItems) => {
            let clone = cloneDeep(lineItems);

            if (lineItems[key].increment_quantity_by)
            {
                const step = clone[key].increment_quantity_by;

                if (quantity > 0 && quantity < step) {
                    quantity = step;
                } else if ((quantity % step) < (step / 2)) {
                    quantity = quantity - (quantity % step);
                } else {
                    quantity = parseInt(quantity) + parseInt(step - (quantity % step));
                }

                clone[key].quantity = quantity.toString();
                clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);
            }

            return clone;
        });
    }, 500), []);

    const updateQuantity = useCallback((key, quantity) => {
        let clone = cloneDeep(lineItems);

        if (quantity == '') {
            quantity = 0;
        }

        clone[key].quantity = quantity.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);

        debounceQuantityUpdate(key, quantity);
    }, [lineItems, location]);

    // Getting the customer from the router parameter.
    useEffect(() => {
        Promise
            .all([
                Api.getCustomer(customerid).then(item => item.data),
                Api.getLocation(locationId).then(item => item.data),
                Api.getShippingMethods().then(results => results.data),
            ])
            .then(([customerData, locationData, shippingMethodsData]) => {
                setCustomer(customerData.data);
                setLocation(locationData.data);
                setAdditionalInfo({
                    ...additionalInfo,
                    customer_notes: locationData.data.notes,
                });
                setShippingMethods(filterShippingMethods(locationData, shippingMethodsData));
                if (customerData.data.shipping_method)
                    setShippingMethod({
                        name: customerData.data.shipping_method?.name ?? '',
                        price: customerData.data.shipping_method?.price ?? '0',
                        key: customerData.data.shipping_method?.shopify_rate_id ?? customerData.data.shipping_method?.key //fallback for old values
                    });
            })


        // This is for recommendations blocks. We could later ignore this or 
        // improve it. 
        Api.getRecommendations('', locationId).then( response => {
            setRecommendations(response.data.data);
        }); 
    }, []);

    const handleShippingMethodChange = useCallback((key) => {
        if (!key || key.length == 0) {
            setShippingMethod({
                name: '',
                price: '',
                key: '',
            });
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            setShippingMethod({
                name: '',
                price: '',
                key: '',
            });
            return;
        }

        setShippingMethod({
            name: method.name,
            price: method.price,
            key: method.key,
        });
    }, [shippingMethod, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        setShippingMethod({
            ...shippingMethod,
            [id]: value,
        });
    }, [shippingMethod]);

    const searchProducts = async () => {
        const { data } = await Api.getProducts(searchInput, locationId)
        const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

        setHasMoreResults(data.meta.last_page > 1);

        const updatedLineItems = data.data.filter(item => ! selectedLineItems.some(({ id }) => id == item.shopify_variant_id))
            .map(variant => ({
                id: variant.shopify_variant_id,
                shopify_product_id: variant.shopify_product_id,
                quantity: "0",
                title: `${variant.sku} - ${variant.product_title}`,
                price: {
                    price: variant.price,
                    total: 0,
                },
                image_url: variant.image_url,
                options: variant.options,
            })).concat(selectedLineItems);

        setLineItems(updatedLineItems);
    }

    const placeDraftOrder = async () => {
        const submitErrors = {};
        const variants = lineItems
            .filter(item => item.quantity > 0)
            .flatMap(item => {
                return [item];
            })
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                original_price: parseFloat(item.price.price),
            }));

        recommendedLineItems.forEach(rec => {
            variants.push({
                id: rec.shopify_variant_id,
                quantity: 1, 
                custom_attributes: [], 
                product_id: rec.shopify_product_id, 
                original_price: parseFloat(rec.price)
            })
        }); 

        const draftOrderData = {
            variants,
            location_id: location.id,
            total_price: lineItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.price);
            }, 0.0),
            customer_notes: location.notes,
            order_notes: additionalInfo.order_notes,
            shipping_method: shippingMethod,
        };

        try {
            let {data} = await Api.placeDraftOrder( draftOrderData );

            Api.updateLocationAfterOrder(location.id, {
                notes: additionalInfo.customer_notes,
            });

            return data.data;

        } catch ( error ) {
            if ( error.response.status == 422 ) {
                Object.keys(error.response.data.errors).forEach( key => {
                    submitErrors[key] = error.response.data.errors[key][0];
                })
            } else {
                throw error;
            }
            setCreating(false);
        } finally {
            setErrors( submitErrors );
            setCreating(false);
        }

        return null;
    }

    const placeAndReview = async () => {
        setCreating(true);

        const draft = await placeDraftOrder();

        if (draft) {
            setTimeout(() => {
                redirect.dispatch(Redirect.Action.ADMIN_PATH, `/draft_orders/${draft.id}`);
            }, 5000);

            return;
        }

        setCreating(false);
    }

    const changeAdditionalInfo = useCallback((value, id) => {
        setAdditionalInfo({
            ...additionalInfo,
            [id]: value,
        });
    }, [additionalInfo]);

    const isRecommendedInLineItems = useCallback(recommendedLineItem => {
        return recommendedLineItems
            .some( item => item.shopify_variant_id == recommendedLineItem.shopify_variant_id ); 
    }); 

    const addRecommendedLineItem = useCallback((recommendedLineItem) => {
        setRecommendedLineItems([
            ...recommendedLineItems, 
            recommendedLineItem
        ]); 
    });

    const removeRecommendedLineITem = useCallback((recommendedLineItem) => {
        const key = recommendedLineItems.findIndex(item => item.shopify_variant_id == recommendedLineItem.shopify_variant_id ); 
        const clone = [...recommendedLineItems];
        clone.splice(key, 1); 
        setRecommendedLineItems(clone);
    }); 

    return (
        <Frame>
            <Page title={`Place Order`} fullWidth>
                <Layout>
                    <Layout.Section>
                    <Card title={`Customer Account`} sectioned>
                        <DescriptionList items={[
                            {term: 'Email', description: customer?.email },
                            {term: 'Phone', description: location?.phone_number || customer?.phone_number },
                            {term: 'Name', description: `${customer?.additional_data.first_name} ${customer?.additional_data.last_name}` },
                            {term: 'Shipping', description: <AddressDetails address={location?.shipping} /> },
                            {term: 'Billing', description: <AddressDetails address={location?.billing} /> },
                        ]} />
                        <Card.Section>
                            <FormLayout>
                                <Select label="Shipping Method"
                                        requiredIndicator={true}
                                        options={[
                                            { label: '--Select Shipping Method--', value: '' },
                                            ...(shippingMethods.map((method) => {
                                                return {
                                                    label: method.label,
                                                    value: method.key + ''
                                                }
                                            }))
                                        ]}
                                        onChange={handleShippingMethodChange}
                                        value={shippingMethod?.key + ''}
                                        id="shipping_method"
                                />

                                { shippingMethod?.key == 'custom' && (
                                    <Stack distribution={"fillEvenly"}>
                                        <TextField requiredIndicator={true} label="Shipping Method Name" id="name" value={shippingMethod.name} onChange={changeCustomShippingMethod} />
                                        <TextField requiredIndicator={true} label="Shipping Method Price" id="price" type={"number"} min={0} step={1.0} value={shippingMethod.price.toString()} onChange={changeCustomShippingMethod} />
                                    </Stack>
                                ) }

                                <TextField multiline={3} label="Customer Notes" value={additionalInfo.customer_notes} id="customer_notes" onChange={changeAdditionalInfo} />
                            </FormLayout>
                            </Card.Section>
                    </Card>

                        <Card title="Line Items">

                            <Card.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                        <TextField label="Search" value={searchInput} onChange={setSearchInput} />
                                        <Button primary submit>Search</Button>
                                    </FormLayout>
                                </Form>
                            </Card.Section>

                        {lineItems.length > 0 && (
                            <>
                                <Card.Section title="Line Items">
                                    <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                        <thead>
                                            <tr>
                                                <th className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left'}}>Item</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '120px' }}>Quantity</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '200px' }}>Unit Price</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '80px' }}>Final Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {lineItems.map( (line_item, key) => {
                                                return (
                                                    <tr key={line_item.id}>
                                                        <td className="Polaris-DataTable__Cell"><Thumbnail source={line_item.image_url} /></td>
                                                        <td className="Polaris-DataTable__Cell" style={{whiteSpace: 'normal'}}>
                                                            <div>
                                                                {line_item.title}
                                                                {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                            </div>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <TextField value={line_item.quantity}
                                                                       min={0}
                                                                       step={1}
                                                                       type="number"
                                                                       onChange={quantity => updateQuantity(key, quantity)}
                                                            />
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            ${line_item.price.price}
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Heading>${parseFloat(line_item.price.total).toFixed(2)}</Heading>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className="Polaris-DataTable__Cell" colSpan="2"><Heading>Totals</Heading></td>
                                                <td className="Polaris-DataTable__Cell">
                                                    <Heading>
                                                        {lineItems.reduce((acc, item) => {
                                                                acc += parseInt(item.quantity) ;
                                                                return acc;
                                                            }, 0)}
                                                    </Heading>
                                                </td>
                                                <td className="Polaris-DataTable__Cell">&nbsp;</td>
                                                <td className="Polaris-DataTable__Cell">
                                                    <Heading>
                                                        {`$${ lineItems.reduce((acc, item) => {
                                                            return acc + parseFloat(item.price.total);
                                                        }, 0.0).toFixed(2)}`}
                                                    </Heading>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </Card.Section>
                            </>
                        )}

                        {hasMoreResults && (
                            <Card.Section>
                                <Banner status="info">
                                    <p>There are more matching products. Please narrow down the search query.</p>
                                </Banner>
                            </Card.Section>
                        )}

                        </Card>

                        <Card sectioned title="Additional Information">
                            <FormLayout>
                                <TextField multiline={3} label="Order Notes" value={additionalInfo.order_notes} id="order_notes" onChange={changeAdditionalInfo} />
                            </FormLayout>
                        </Card>

                        { recommendations != null && recommendations.length > 0 && lineItems.filter(item => item.quantity > 0).length > 0 && (
                            <VerticalStack gap="4">
                                <div style={{marginTop: '30px' }}>
                                    <Text variant="headingLg" as="h3">Recommendations for this order</Text>
                                </div>
                                <HorizontalGrid gap="4" columns={recommendations.length > 4 ? 4 : recommendations.length }>
                                    { recommendations.slice(0, 4).map(recommendation => (
                                        <div><MediaCard
                                            portrait
                                            title={ `${recommendation.product_title} - $${recommendation.price}` }
                                            primaryAction={
                                                isRecommendedInLineItems(recommendation)
                                                    ? { content: 'Remove', destructive: true, onAction: () => {removeRecommendedLineITem(recommendation)}, }
                                                    : { content: 'Add Product', onAction: () => {addRecommendedLineItem(recommendation)}, }
                                            }
                                            secondaryAction={{ content: 'Learn More',  onAction: () => {} }}
                                            description="lorem ipsum dolor sit amet, conserctum dolor lorem ipsum sit."
                                            size="small"
                                            >
                                            <img alt="" width="100%" height="100%" style={{ objectFit: 'cover', objectPosition: 'center'}} src={recommendation.image_url} />
                                        </MediaCard></div>
                                    ))}
                                    
                                </HorizontalGrid>
                            </VerticalStack>
                    )}

                        { customer?.cards == null && (
                            <Banner
                                title="No credit card added to this customer"
                                status="warning"
                                >
                                <TextContainer>
                                    <p>The customer you are placing an order for doesn't have a credit card added to his profile. Please add a credit card first.</p>
                                </TextContainer>
                                </Banner>
                        )}

                        { Object.keys(errors).length > 0 && (
                            <Banner
                            title="There were some errors creating the order."
                            status="critical"
                            >
                                <List>
                                    {Object.keys(errors).map(key => <List.Item><strong>{key.replace('_', ' ')}</strong>: {errors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        )}

                    <PageActions
                        primaryAction={[
                            {
                                content: 'Place Order and Review',
                                onAction: placeAndReview,
                                loading: creating
                            },
                        ]}
                    />
                    </Layout.Section>


                    <Layout.Section secondary sectioned>
                        <LegacyCard>
                            <LegacyCard.Section>
                                <LegacyStack vertical>
                                    <Text variant="headingMd" as="h4">Recent Orders</Text>
                                    <Link url="#" monochrome>123123456</Link>
                                    <Link url="#" monochrome>123123456</Link>
                                    <Link url="#" monochrome>123123456</Link>
                                    <Link url="#" monochrome>View All</Link>
                                </LegacyStack>
                            </LegacyCard.Section>
                            <LegacyCard.Section>
                                <LegacyStack vertical>
                                    <Text variant="headingMd" as="h4">Account Status</Text>
                                    <Text as="p">Offline Only</Text>
                                    <Link url="#" monochrome>Send Invite</Link>
                                </LegacyStack>
                            </LegacyCard.Section>
                            <LegacyCard.Section>
                                <LegacyStack vertical>
                                    <Text variant="headingMd" as="h4">CLV</Text>
                                    <Text as="p">$123,042</Text>
                                    <Text variant="headingMd" as="h4">Last 90 Days</Text>
                                    <Text as="p">$17,230</Text>
                                </LegacyStack>
                            </LegacyCard.Section>
                            <LegacyCard.Section>
                                <LegacyStack vertical>
                                    <Text variant="headingMd" as="h4">Potential Leveling Categories</Text>
                                    <Link external monochrome url="#">Filtration</Link>
                                    <Link external monochrome url="#">Pumps</Link>
                                    <Link external monochrome url="#">Valves</Link>
                                </LegacyStack>
                            </LegacyCard.Section>
                            <LegacyCard.Section>
                                <LegacyStack vertical>
                                    <Text variant="headingMd" as="h4">Customer Tier</Text>
                                    <img src="https://cdn.shopify.com/s/files/1/0763/1136/3891/files/diamonds.png?v=1684784778" alt="" />
                                </LegacyStack>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </Page>
        </Frame>
    );
}

export default Orders;
